import { Box } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { createFormatter, formatNumber, NumberFormatPreset, useCurrency } from '@procureai/ui-mui';
import { isNil } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { NULLISH_CELL_VALUE } from '../../DataGridCustom.types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { StatusUnion } from '~/common/types';
import { MaskedValue, MaskedValueVariant } from '~/components/MaskedValue';
import { colorFromStatus } from '~/util/conversion';

import { StrikeThrough } from './CurrencyCell.styled';

export type CurrencyCellValue = Union.Nullable<number>;

export interface CurrencyCellProps<R extends GridValidRowModel = any> {
  value: CurrencyCellValue;
  row?: R;
  valueStrikeThrough?: Union.Nullable<number>;
  currency?: Union.Nullable<string>;
  status?: Union.Nullable<StatusUnion>;
  isMasked?: boolean;
  showApproximationTooltip?: boolean;
}

const InnerCurrencyCell = <R extends GridValidRowModel = any>({
  value,
  currency,
  status,
  valueStrikeThrough,
  showApproximationTooltip,
}: CurrencyCellProps<R>) => {
  const { i18n } = useTranslation();
  const currencyCode = currency || undefined;
  const { format } = useCurrency({
    code: currencyCode,
  });
  const formatExact = useMemo<(amount: number) => string>(() => {
    if (currencyCode) {
      return createFormatter(i18n.language, currencyCode).formatNumber(NumberFormatPreset.CURRENCY_EXTENDED);
    }

    return formatNumber(NumberFormatPreset.DECIMAL_EXTENDED) as (amount: number) => string;
  }, [currencyCode, i18n.language]);

  const renderFormatted = (amount: number) => {
    const rounded = format(amount);
    if (!showApproximationTooltip) {
      return rounded;
    }

    const numericAmount = Number(amount);
    const isRounded = Number.isFinite(numericAmount) ? Number(numericAmount.toFixed(2)) !== numericAmount : false;
    if (!isRounded) {
      return rounded;
    }

    const exact = formatExact(amount);
    return (
      <Tooltip>
        <TooltipTrigger delay={0} render={<span>{`~${rounded}`}</span>} />
        <TooltipContent>{exact}</TooltipContent>
      </Tooltip>
    );
  };
  const valueCurrent = isNil(value) ? valueStrikeThrough : value;

  if (!isNil(valueCurrent)) {
    const statusColor = status ? colorFromStatus(status) : undefined;
    const additional =
      isNil(valueStrikeThrough) || valueCurrent === valueStrikeThrough ? null : (
        <>
          (<StrikeThrough>{renderFormatted(valueStrikeThrough)}</StrikeThrough>)
        </>
      );
    return (
      <Box
        component="span"
        sx={{
          color: statusColor && `${statusColor}.main`,
        }}
      >
        {renderFormatted(valueCurrent)} {additional}
      </Box>
    );
  }
  return <>{NULLISH_CELL_VALUE}</>;
};

export const CurrencyCell = <R extends GridValidRowModel = any>({
  value,
  currency,
  status,
  valueStrikeThrough,
  isMasked,
  showApproximationTooltip,
}: CurrencyCellProps<R>) => {
  return (
    <MaskedValue isMasked={isMasked} variant={MaskedValueVariant.CURRENCY}>
      <InnerCurrencyCell
        value={value}
        currency={currency}
        status={status}
        valueStrikeThrough={valueStrikeThrough}
        showApproximationTooltip={showApproximationTooltip}
      />
    </MaskedValue>
  );
};
